import { usePathname, useSearch as useSearchParams, RouterLink as Link } from "../router";
import { useMemo, type ReactNode } from "react";
import { cn } from "../utils";
import { pickActiveTarget } from "../nav-tabs";

/**
 * SectionNavRail — a grouped, route-driven vertical nav rail meant to sit at the
 * RIGHT of a surface's content (in-content, not global chrome), replacing an
 * on-page horizontal tab strip when a surface has more sections than a tab row
 * can carry. It is the right-positioned generalization of the left settings rail
 * (`SettingsNav`) + the product/database rails (`SectionRail`): same icon+label
 * rows, same `nav-active` indicator, same query-aware longest-prefix matching
 * (`pickActiveTarget`) — so the active item is chosen identically everywhere.
 *
 * The differentiator vs `SettingsNav`: every group is always shown (no
 * click-to-expand), and a group may carry a one-line `caption` that NAMES the
 * subject of its sections — e.g. "Excellent's agent workforce" vs "your Claude
 * Code · ~/.claude" on Agents ▸ Analytics, so the rail itself disambiguates whose
 * data each section shows (the signpost a flat tab strip can't give).
 *
 * Pure presentation: the page/layout owns width + positioning via `className`
 * (e.g. `sticky top-2 w-56`). It is NOT a third tab style — it is a rail, the
 * sanctioned non-tab route nav (Frontend conventions rule 13 governs tabs).
 */
export interface SectionNavItem {
  href: string;
  label: string;
  /** Pre-rendered icon JSX (so a lucide ref doesn't cross server→client). */
  icon?: ReactNode;
  /** Override the path used for active matching (defaults to href). */
  activeHref?: string;
}

export interface SectionNavGroup {
  /** Group header — the section family ("Fleet", "You", "Library", "Manage"). */
  label?: string;
  /** One-line subject hint under the header — names the agent/subject. */
  caption?: string;
  items: SectionNavItem[];
}

export interface SectionNavRailProps {
  groups: SectionNavGroup[];
  /** Width + positioning (the layout owns these), e.g. `sticky top-2 w-56`. */
  className?: string;
  /** Accessible label for the nav landmark. */
  ariaLabel?: string;
}

export function SectionNavRail({
  groups,
  className,
  ariaLabel = "Sections",
}: SectionNavRailProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const navParams = useMemo(
    () => new URLSearchParams(searchParams?.toString() ?? ""),
    [searchParams],
  );

  // One winner across ALL items (every group), so a deeper leaf (e.g.
  // `…/fleet/quality`, `…/you/sessions`) beats its parent (`…/fleet`, `…/you`)
  // and the bare Overview (`/agents/analytics`) only lights on its own page.
  const activeTarget = useMemo(() => {
    const targets = groups.flatMap((g) =>
      g.items.map((it) => it.activeHref ?? it.href),
    );
    return pickActiveTarget(pathname, navParams, targets);
  }, [groups, pathname, navParams]);

  return (
    <nav aria-label={ariaLabel} className={cn("flex flex-col", className)}>
      {groups.map((group, gi) => (
        <div key={group.label ?? gi} className={gi > 0 ? "mt-5" : undefined}>
          {group.label ? (
            <div className="px-2.5 type-subtext font-medium tracking-tight text-foreground/80">
              {group.label}
            </div>
          ) : null}
          {group.caption ? (
            <div className="px-2.5 pb-1.5 pt-0.5 type-subtext text-muted-foreground/60">
              {group.caption}
            </div>
          ) : (
            <div className="pb-1" />
          )}
          <div className="space-y-px">
            {group.items.map((item) => {
              const target = item.activeHref ?? item.href;
              const active = target === activeTarget;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 type-body tracking-tight transition-colors",
                    active
                      ? "nav-active font-medium text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                  )}
                >
                  {item.icon ? (
                    <span
                      className={cn(
                        "inline-flex shrink-0",
                        active ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      {item.icon}
                    </span>
                  ) : null}
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
